<template>
  <div class="contents">
    <div class="layout">
      <div class="breadcrumb">
        <div class="breadcrumb-item">시스템 관리</div>
        <div class="breadcrumb-item active">관리자 계정 관리</div>
      </div>

      <div class="page-header">
        <h1 class="page-title">관리자 계정 관리</h1>
        <div class="page-actions">
          <button 
            v-permission="'ADMIN_WRITE'" 
            @click="openCreateModal" 
            class="btn btn-primary"
          >
            <i class="icon-plus"></i>
            관리자 추가
          </button>
        </div>
      </div>

      <div class="white-panel mt20">
        <div class="table-wrap">
          <div class="scroll-box">
            <table class="table-type1">
              <colgroup>
                <col style="width: 5%;">  <!-- NO -->
                <col style="width: 15%;"> <!-- 계정ID -->
                <col style="width: 15%;"> <!-- 이름 -->
                <col style="width: 20%;"> <!-- 이메일 -->
                <col style="width: 15%;"> <!-- 역할 -->
                <col style="width: 15%;"> <!-- 생성일 -->
                <col style="width: 15%;"> <!-- 액션 -->
              </colgroup>
              <thead>
                <tr>
                  <th>NO</th>
                  <th>계정 ID</th>
                  <th>이름</th>
                  <th>이메일</th>
                  <th>역할</th>
                  <th>생성일</th>
                  <th>관리</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(admin, index) in admins" :key="admin.id">
                  <td>{{ index + 1 }}</td>
                  <td>{{ admin.accountId }}</td>
                  <td>{{ admin.name }}</td>
                  <td>{{ admin.email }}</td>
                  <td>
                    <span class="badge" :class="getRoleBadgeClass(admin.roleName)">
                      {{ admin.roleName }}
                    </span>
                  </td>
                  <td>{{ formatDate(admin.createdAt) }}</td>
                  <td>
                    <div class="action-buttons">
                      <button 
                        v-permission="'ADMIN_WRITE'" 
                        @click="openEditModal(admin)" 
                        class="btn-sm btn-outline"
                      >
                        수정
                      </button>
                      <button 
                        v-if="admin.isDeletable" 
                        v-permission="'ADMIN_DELETE'" 
                        @click="confirmDelete(admin)" 
                        class="btn-sm btn-danger"
                      >
                        삭제
                      </button>
                      <span v-else class="text-muted text-sm">삭제불가</span>
                    </div>
                  </td>
                </tr>
                <tr v-if="admins.length === 0">
                  <td colspan="7" class="text-center text-muted">
                    등록된 관리자가 없습니다.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- 관리자 생성/수정 모달 -->
      <div v-if="showModal" class="modal-overlay" @click="closeModal">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <h3>{{ modalMode === 'create' ? '관리자 추가' : '관리자 수정' }}</h3>
            <button @click="closeModal" class="modal-close">&times;</button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="submitForm">
              <div class="form-group">
                <label for="accountId">계정 ID *</label>
                <input 
                  id="accountId"
                  v-model="form.accountId" 
                  :disabled="modalMode === 'edit'"
                  type="text" 
                  class="form-control" 
                  required
                >
              </div>
              <div class="form-group">
                <label for="name">이름 *</label>
                <input 
                  id="name"
                  v-model="form.name" 
                  type="text" 
                  class="form-control" 
                  required
                >
              </div>
              <div class="form-group">
                <label for="email">이메일 *</label>
                <input 
                  id="email"
                  v-model="form.email" 
                  type="email" 
                  class="form-control" 
                  required
                >
              </div>
              <div class="form-group">
                <label for="password">비밀번호 {{ modalMode === 'edit' ? '(변경시에만 입력)' : '*' }}</label>
                <input 
                  id="password"
                  v-model="form.password" 
                  type="password" 
                  class="form-control" 
                  :required="modalMode === 'create'"
                >
              </div>
              <div class="form-group">
                <label for="roleId">역할 *</label>
                <select 
                  id="roleId"
                  v-model="form.roleId" 
                  class="form-control" 
                  required
                >
                  <option value="">역할을 선택하세요</option>
                  <option 
                    v-for="role in availableRoles" 
                    :key="role.id" 
                    :value="role.id"
                  >
                    {{ role.name }} - {{ role.description }}
                  </option>
                </select>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button @click="closeModal" class="btn btn-secondary">취소</button>
            <button @click="submitForm" class="btn btn-primary">
              {{ modalMode === 'create' ? '추가' : '수정' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, reactive } from 'vue'
import { apiClient } from '@/services/auth-header'
import { useToast } from '@/composables/useToast'

const toast = useToast()

// 데이터
const admins = ref([])
const availableRoles = ref([])
const loading = ref(false)

// 모달 관련
const showModal = ref(false)
const modalMode = ref('create') // 'create' or 'edit'
const form = reactive({
  id: null,
  accountId: '',
  name: '',
  email: '',
  password: '',
  roleId: null
})

// 페이지 로드 시 데이터 조회
onMounted(async () => {
  await Promise.all([
    fetchAdmins(),
    fetchAvailableRoles()
  ])
})

// 관리자 목록 조회
const fetchAdmins = async () => {
  try {
    loading.value = true
    const response = await apiClient.get('/admin/users')
    admins.value = response.data
  } catch (error) {
    toast.error('관리자 목록을 불러오는데 실패했습니다.')
  } finally {
    loading.value = false
  }
}

// 사용 가능한 역할 목록 조회
const fetchAvailableRoles = async () => {
  try {
    const response = await apiClient.get('/admin/users/available-roles')
    availableRoles.value = response.data
  } catch (error) {
    console.error('역할 목록 조회 실패:', error)
  }
}

// 생성 모달 열기
const openCreateModal = () => {
  modalMode.value = 'create'
  resetForm()
  showModal.value = true
}

// 수정 모달 열기
const openEditModal = (admin) => {
  modalMode.value = 'edit'
  form.id = admin.id
  form.accountId = admin.accountId
  form.name = admin.name
  form.email = admin.email
  form.password = ''
  form.roleId = admin.roleId
  showModal.value = true
}

// 모달 닫기
const closeModal = () => {
  showModal.value = false
  resetForm()
}

// 폼 초기화
const resetForm = () => {
  form.id = null
  form.accountId = ''
  form.name = ''
  form.email = ''
  form.password = ''
  form.roleId = null
}

// 폼 제출
const submitForm = async () => {
  try {
    if (modalMode.value === 'create') {
      await apiClient.post('/admin/users', {
        accountId: form.accountId,
        name: form.name,
        email: form.email,
        password: form.password,
        roleId: form.roleId
      })
      toast.success('관리자가 성공적으로 추가되었습니다.')
    } else {
      const updateData = {
        name: form.name,
        email: form.email,
        roleId: form.roleId
      }
      if (form.password.trim()) {
        updateData.password = form.password
      }
      
      await apiClient.put(`/admin/users/${form.id}`, updateData)
      toast.success('관리자 정보가 성공적으로 수정되었습니다.')
    }
    
    closeModal()
    await fetchAdmins()
  } catch (error) {
    const errorMessage = error.response?.data?.message || 
                        (modalMode.value === 'create' ? '관리자 추가에 실패했습니다.' : '관리자 수정에 실패했습니다.')
    toast.error(errorMessage)
  }
}

// 삭제 확인
const confirmDelete = (admin) => {
  if (confirm(`'${admin.name}' 관리자를 삭제하시겠습니까?`)) {
    deleteAdmin(admin.id)
  }
}

// 관리자 삭제
const deleteAdmin = async (id) => {
  try {
    await apiClient.delete(`/admin/users/${id}`)
    toast.success('관리자가 성공적으로 삭제되었습니다.')
    await fetchAdmins()
  } catch (error) {
    const errorMessage = error.response?.data?.message || '관리자 삭제에 실패했습니다.'
    toast.error(errorMessage)
  }
}

// 유틸리티 함수들
const formatDate = (dateString) => {
  if (!dateString) return ''
  return new Date(dateString).toLocaleDateString('ko-KR')
}

const getRoleBadgeClass = (roleName) => {
  switch (roleName) {
    case 'SUPER_ADMIN':
      return 'badge-danger'
    case 'ADMIN':
      return 'badge-warning'
    default:
      return 'badge-secondary'
  }
}
</script>

<style scoped>
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-title {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
}

.page-actions {
  display: flex;
  gap: 10px;
}

.action-buttons {
  display: flex;
  gap: 5px;
  align-items: center;
}

.badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  text-align: center;
}

.badge-danger {
  background-color: #dc3545;
  color: white;
}

.badge-warning {
  background-color: #ffc107;
  color: #212529;
}

.badge-secondary {
  background-color: #6c757d;
  color: white;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #dee2e6;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.modal-close {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-body {
  padding: 20px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 20px;
  border-top: 1px solid #dee2e6;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
  color: #333;
}

.form-control {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.form-control:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.btn-primary {
  background-color: #007bff;
  color: white;
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
}

.btn-danger {
  background-color: #dc3545;
  color: white;
}

.btn-outline {
  background-color: transparent;
  color: #007bff;
  border: 1px solid #007bff;
}

.btn-sm {
  padding: 4px 8px;
  font-size: 12px;
}

.text-center {
  text-align: center;
}

.text-muted {
  color: #6c757d;
}

.text-sm {
  font-size: 12px;
}
</style>